import { gql, useQuery } from "@apollo/client";
import type { IMetaobjectQuery } from "@repo/shopify/queries/metaobject-gql";
import { metaobjectQuery } from "@repo/shopify/queries/metaobject-gql";
import type { Metaobject, MetaobjectEdge } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import type { ISortOption } from "@const/constant";
import { SORT } from "@const/constant";
import { DEFAULT_TITLE } from "@const/meta";
import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import { useLayoutStore } from "@store/layout";

export default function Recruit() {
  const { openToast, setMeta, showFooter } = useLayoutStore();

  const [searchParams, setSearchParams] = useSearchParams();
  const sortParam = searchParams.get("sort") || "old";

  const [sort, setSort] = useState<ISortOption>(sortParam === "new" ? SORT[1] : SORT[0]);

  const { data, loading } = useQuery<IMetaobjectQuery>(gql(metaobjectQuery.query), {
    variables: { type: "recruit", first: 50 },
    onError: (error) => {
      openToast(NOTIFICATIONS.ERROR_SERVER);
      throw new Error(error.message);
    },
  });

  useEffect(() => {
    setMeta({ title: `Recruit - ${DEFAULT_TITLE}` });
    showFooter(true);
    window.scrollTo(0, 0);
  }, []);

  const getValue = (node: Metaobject, key: string) => {
    return node.fields.find((item) => item.key === key);
  };

  const recruits = useMemo(() => {
    const edges = data?.metaobjects.edges || [];
    return [...edges].sort(({ node: a }: MetaobjectEdge, { node: b }: MetaobjectEdge) => {
      const dateA = getValue(a, "date")?.value || "";
      const dateB = getValue(b, "date")?.value || "";
      return sort.reverse ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });
  }, [data, sort]);

  return (
    <>
      {loading || !data ? (
        <Loading type="page" />
      ) : (
        <>
          <section className="pc:col-span-12 col-span-6 mb-2.5 flex justify-between">
            <h2 className="text-title">Recruit</h2>
            <Button
              as="text"
              onClick={() => {
                const sortIndex = SORT.findIndex((option) => option.label === sort.label);
                setSort(SORT[sortIndex === 0 ? 1 : 0]);
                setSearchParams({ sort: sortIndex === 0 ? "new" : "old" });
              }}
            >
              {sort.label}
            </Button>
          </section>
          <section className="pc:col-span-12 col-span-6 flex flex-col gap-10">
            {recruits.map(({ node: recruit }: MetaobjectEdge) => (
              <article className="tnt-gap-x pc:grid-cols-12 pc:gap-y-0 grid grid-cols-6 gap-y-2.5" key={recruit.handle}>
                <div className="col-span-6 flex flex-col gap-5">
                  <Link
                    className="text-title flex! flex-col whitespace-pre-line"
                    href={`${URLS.RECRUIT}/${recruit.handle}`}
                  >
                    {getValue(recruit, "title")?.value?.replace(/\\n/g, "\n")}
                  </Link>
                  <div className="tnt-gap-x grid grid-cols-6 gap-y-1">
                    <span className="pc:col-span-1 col-span-2">Workplace</span>
                    <span className="pc:col-span-5 col-span-4 w-fit">{getValue(recruit, "location")?.value}</span>
                  </div>
                  <Link as="text" className="w-fit" href={`${URLS.RECRUIT}/${recruit.handle}`}>
                    More Info →
                  </Link>
                </div>
              </article>
            ))}
          </section>
        </>
      )}
    </>
  );
}
